import { IMsalContext } from "@azure/msal-react";
import axios, { AxiosResponse, AxiosError } from "axios";
import { getAccessToken } from "config/token";
import { NEXUS_API_BASE_URL_V2 } from "const";
import { EXCLUDED_FILTER_PROFILE_TYPES } from "utils/normalizeSpaces";

export interface FilterOptionsResponse {
    success: boolean;
    data?: {
        project_types: string[];
        bucket_types: string[];
        regions: string[];
        profile_types?: string[];
    };
    error?: string;
    status?: number;
}

export interface FilterOptionsParams {
    scope: string;
    domain_name: string;
    region_name: string;
}

export const getFilterOptions = async (
    msalInstance: IMsalContext, 
    params?: FilterOptionsParams,
): Promise<FilterOptionsResponse> => {
    try {
        const token = await getAccessToken(msalInstance);
        const bearerToken = "Bearer " + (token == undefined ? "" : token.token);
        const requestURL = `${NEXUS_API_BASE_URL_V2}/nexus/spaces/filter-options`;

        const config = {
            headers: { Authorization: bearerToken },
            params: {
                ...(params?.scope && { scope: params.scope }),
                ...(params?.domain_name && { domain_name: params.domain_name }),
                ...(params?.region_name && { region_name: params.region_name }),
            },
        };

        const response: AxiosResponse = await axios.get(requestURL, config);
        const { data } = response;

        return { 
            success: true, 
            data: {
                project_types: data.project_types || [],
                bucket_types: data.bucket_types || [],
                regions: data.regions || [],
                // Normalize each item: API may return string keys or {text, value} objects
                profile_types: ((data.profile_types || []) as Array<string | { value: string }>)
                    .map((item) => (typeof item === "string" ? item : item.value))
                    .filter((key) => !EXCLUDED_FILTER_PROFILE_TYPES.includes(key)),
            },
        };
    } catch (error) {
        console.error("Error fetching filter options:", error);

        if (axios.isAxiosError(error)) {
            const status = error.response?.status;
            const responseData = error.response?.data;
            
            let errorMessage: string;
            if (responseData?.context?.message) {
                // Handle new API error payload structure
                errorMessage = responseData.context.message;
            } else if (responseData?.message) {
                // Handle standard error message
                errorMessage = responseData.message;
            } else {
                // Generic fallback
                errorMessage = error.message || "Failed to fetch filter options";
            }
            
            return { success: false, error: errorMessage, status };
        } else {
            return { success: false, error: "An unexpected error occurred while fetching filter options" };
        }
    }
};
